export type SearchIndexOptions<T> = {
  indexedKeywordsFn: (entry: T) => string[];
};
export type SearchEntry<T> = {
  entry: T;
  normalizedKeywords: string[];
};
export type SearchIndex<T> = SearchEntry<T>[];

export function createSearchIndex<T>(
  searchEntries: T[],
  options: SearchIndexOptions<T>,
): SearchIndex<T> {
  const searchIndex: SearchIndex<T> = [];

  searchEntries.forEach(entry => {
    const rawKeywords = options.indexedKeywordsFn(entry);
    const normalizedKeywords = rawKeywords.map(keyword => normalizeKeyword(keyword));

    searchIndex.push({
      entry,
      normalizedKeywords,
    });
  });
  return searchIndex;
}

export function normalizeKeyword(keyword: string) {
  if (typeof keyword !== 'string') {
    return '';
  }
  // Lowercase and remove non-standard characters
  return keyword
    .trim()
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function searchInIndex<T>(searchIndex: SearchIndex<T>, searchText: string): T[] {
  const normalizedSearchText = normalizeKeyword(searchText);
  const normalizedSearchParts = normalizedSearchText.split(' ');
  const results: T[] = [];

  searchIndex.forEach(searchEntry => {
    if (
      normalizedSearchParts.every(search =>
        searchEntry.normalizedKeywords.some(keyword => keyword.includes(search)),
      )
    ) {
      results.push(searchEntry.entry);
    }
  });

  return results;
}

export function getAllSearchEntries<T>(searchIndex: SearchIndex<T>): T[] {
  return searchIndex.map(searchEntry => searchEntry.entry);
}

// Scored search types and functions
export type WeightedKeyword = {
  keyword: string;
  weight: number;
};

export type ScoredSearchIndexOptions<T> = {
  weightedKeywordsFn: (entry: T) => WeightedKeyword[];
};

export type ScoredSearchEntry<T> = {
  entry: T;
  weightedKeywords: { normalized: string; weight: number }[];
};

export type ScoredSearchIndex<T> = ScoredSearchEntry<T>[];

export type ScoredResult<T> = {
  entry: T;
  score: number;
};

export function createScoredSearchIndex<T>(
  searchEntries: T[],
  options: ScoredSearchIndexOptions<T>,
): ScoredSearchIndex<T> {
  return searchEntries.map(entry => ({
    entry,
    weightedKeywords: options.weightedKeywordsFn(entry).map(wk => ({
      normalized: normalizeKeyword(wk.keyword),
      weight: wk.weight,
    })),
  }));
}

export function searchInIndexWithScores<T>(
  searchIndex: ScoredSearchIndex<T>,
  searchText: string,
): ScoredResult<T>[] {
  const normalizedSearchText = normalizeKeyword(searchText);
  if (!normalizedSearchText) {
    return [];
  }

  const searchParts = normalizedSearchText.split(' ').filter(Boolean);
  const results: ScoredResult<T>[] = [];

  searchIndex.forEach(searchEntry => {
    let totalScore = 0;
    let allPartsMatch = true;

    for (const searchPart of searchParts) {
      let partScore = 0;
      for (const wk of searchEntry.weightedKeywords) {
        if (wk.normalized.includes(searchPart)) {
          partScore = Math.max(partScore, wk.weight);
        }
      }
      if (partScore === 0) {
        allPartsMatch = false;
        break;
      }
      totalScore += partScore;
    }

    if (allPartsMatch) {
      results.push({ entry: searchEntry.entry, score: totalScore });
    }
  });

  return results.sort((a, b) => b.score - a.score);
}

export function getAllScoredSearchEntries<T>(searchIndex: ScoredSearchIndex<T>): T[] {
  return searchIndex.map(searchEntry => searchEntry.entry);
}
